import {
  isStoryEditorEntryReady,
  isStoryReady,
} from '@src/components/Dashboard/utils';
import {
  useDashboard,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import CloseIcon from '../svgs/CloseIcon';

/**
 * Dev/measurement overlay for the Faster TTFE effort (PLAT-11223): a stopwatch
 * per upload started this session, pinned to the lower-right corner. Starts at
 * queue time, marks when the story becomes editable (transcription-ready) and
 * when the full pipeline finishes, and stays on screen until dismissed so the
 * timings can be read off after the fact.
 */

type StopwatchMarks = {
  editorReadyAt?: number;
  fullyReadyAt?: number;
};

const formatElapsed = (ms: number): string => {
  const clamped = Math.max(0, ms);
  const totalSec = Math.floor(clamped / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const hundredths = String(Math.floor((clamped % 1000) / 10)).padStart(2, '0');
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}.${hundredths}` : `${mm}:${ss}.${hundredths}`;
};

const UploadStopwatchOverlay = observer(() => {
  const uploadFlowStore = useUserStoryUploadFlow();
  const dashboardStore = useDashboard();

  // storyId/key → freeze marks; separate from the stores so a phase record
  // moving to 'complete' (or being cleaned up) doesn't lose the readings.
  const [marks, setMarks] = useState<Record<string, StopwatchMarks>>({});
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // Re-render tick while any stopwatch is still running.
  const [, setNow] = useState(Date.now());

  // Persist observed uploads locally: later phases ('complete') carry no
  // timestamps and cleanup can remove record entries entirely, but the
  // stopwatch card must survive until explicitly dismissed.
  const [tracked, setTracked] = useState<
    Record<string, { start: number; fileName?: string }>
  >({});

  // Derive live uploads during render — the observable read here is what
  // makes this observer re-render when the phase record changes; the effect
  // below only commits them into persistent state.
  //
  // One upload can appear under several keys over its lifetime (temp modal
  // key while the blank story is created, then the story id — and the two
  // phases carry different timestamps). A story-id key wins over a temp key
  // for the same fileName, and the earliest observed start is kept so the
  // stopwatch origin stays at queue time.
  const modalToStory = uploadFlowStore.modalIdToStoryId;
  const storyKeyed = new Set(Object.values(modalToStory));
  const additions: Record<string, { start: number; fileName?: string }> = {};
  const replacedKeys: string[] = [];
  for (const [key, phase] of Object.entries(
    uploadFlowStore.storyUploadPhaseRecord,
  )) {
    if (tracked[key]) continue;
    const start =
      'queuedAt' in phase
        ? phase.queuedAt
        : 'startedAt' in phase
          ? phase.startedAt
          : undefined;
    if (!start) continue;
    const fileName = 'fileName' in phase ? phase.fileName : undefined;
    let earliest = start;
    let supersededByExisting = false;
    for (const [k, t] of Object.entries(tracked)) {
      if (t.fileName !== fileName) continue;
      earliest = Math.min(earliest, t.start);
      // Same file already tracked under a story id — a late temp key (or a
      // re-keyed record) must not spawn a second card.
      if (storyKeyed.has(k) && !storyKeyed.has(key)) {
        supersededByExisting = true;
      } else {
        replacedKeys.push(k);
      }
    }
    if (supersededByExisting) continue;
    additions[key] = { start: earliest, fileName };
  }

  useEffect(() => {
    if (Object.keys(additions).length === 0) return;
    setTracked(prev => {
      const next = { ...prev, ...additions };
      for (const k of replacedKeys) delete next[k];
      return next;
    });
  });

  const merged = { ...tracked, ...additions };
  for (const k of replacedKeys) delete merged[k];
  const entries = Object.entries(merged)
    .filter(([key]) => !dismissed.has(key))
    .map(([key, t]) => ({ key, start: t.start, fileName: t.fileName }));

  // Capture ready-transition timestamps once per story.
  useEffect(() => {
    if (entries.length === 0) return;
    const next: Record<string, StopwatchMarks> = {};
    for (const entry of entries) {
      const storyId = modalToStory[entry.key] ?? entry.key;
      const story = dashboardStore.allStories.find(s => s.id === storyId);
      if (!story) continue;
      const current = marks[entry.key] ?? {};
      const editorReadyAt =
        current.editorReadyAt ??
        (isStoryEditorEntryReady(story) ? Date.now() : undefined);
      const fullyReadyAt =
        current.fullyReadyAt ?? (isStoryReady(story) ? Date.now() : undefined);
      if (
        editorReadyAt !== current.editorReadyAt ||
        fullyReadyAt !== current.fullyReadyAt
      ) {
        next[entry.key] = { editorReadyAt, fullyReadyAt };
      }
    }
    if (Object.keys(next).length > 0) {
      setMarks(prev => ({ ...prev, ...next }));
    }
  }, [entries, marks, modalToStory, dashboardStore.allStories]);

  const anyRunning = entries.some(e => !marks[e.key]?.fullyReadyAt);
  useEffect(() => {
    if (!anyRunning) return undefined;
    let frame = 0;
    const tick = () => {
      setNow(Date.now());
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [anyRunning]);

  if (entries.length === 0) return null;

  return (
    <Stack data-testid="upload-stopwatch-overlay">
      {entries.map(entry => {
        const mark = marks[entry.key] ?? {};
        const editingMs = (mark.editorReadyAt ?? Date.now()) - entry.start;
        const fullMs = (mark.fullyReadyAt ?? Date.now()) - entry.start;
        return (
          <Card key={entry.key}>
            <CardHeader>
              <Title title={entry.fileName}>{entry.fileName || 'Upload'}</Title>
              <DismissButton
                aria-label="Dismiss stopwatch"
                onClick={() =>
                  setDismissed(prev => new Set([...prev, entry.key]))
                }
              >
                <CloseIcon width="10" height="10" />
              </DismissButton>
            </CardHeader>
            <Row data-done={!!mark.editorReadyAt || undefined}>
              <RowLabel>Ready to Edit</RowLabel>
              <RowTime>{formatElapsed(editingMs)}</RowTime>
            </Row>
            <Row data-done={!!mark.fullyReadyAt || undefined}>
              <RowLabel>Fully Processed</RowLabel>
              <RowTime>{formatElapsed(fullMs)}</RowTime>
            </Row>
          </Card>
        );
      })}
    </Stack>
  );
});

const Stack = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 100001;
`;

const Card = styled.div`
  min-width: 220px;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: var(--card, #1a1a1a);
  border: 1px solid var(--border, #333);
  box-shadow: var(--elevation-toast);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const Title = styled.span`
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 2px;
  cursor: pointer;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  opacity: 0.75;
  &[data-done] {
    opacity: 1;
    color: var(--primary);
  }
`;

const RowLabel = styled.span`
  font-size: 11px;
  color: var(--muted-foreground, #999);
`;

const RowTime = styled.span`
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: var(--font-weight-semibold);
  color: inherit;
`;

export default UploadStopwatchOverlay;
